// This file contains all of the routes for Starry Night.
// The code at the bottom just sets up a "middleware" regime so that we can execute functions before loading a route.
// The only middleware currently in use is checking to see if the user is signed in or not.
// For example, you might see this in a route definition:
//
// meta: {
//     middleware: [authenticated]
// }
//
// This array of functions (`authenticated`, in this case) will execute in order.
// If at any point, one of the functions fails, you can choose to take some other action.
// For example, you may want to redirect the user to the homepage if they're not authenticated.

import Vue from 'vue';
import Router from 'vue-router';

import authenticated from './middleware/authenticated';
import anonymous from './middleware/anonymous';

import Home from './views/Home.vue';
import Support from './views/Support.vue';
import About from './views/About.vue';
import Library from './views/Library.vue';
import Product from './views/product/Product.vue';
import ProductContents from './views/product/ProductContents.vue';
import ProductExercises from './views/product/ProductExercises.vue';
import ProductAssignments from './views/product/ProductAssignments.vue';
import ProductSimulator from './views/product/ProductSimulator.vue';

import Assignment from './views/assignment/Assignment.vue';
import Chapter from './views/chapter/Chapter.vue';

Vue.use(Router);

const router = new Router({
    mode: 'history',
    scrollBehavior(){
        return {x: 0, y: 0};
    },
    routes: [
        {
            path: '',
            name: 'home',
            component: Home
        },
        {
            path: '/support',
            name: 'support',
            component: Support
        },
        {
            path: '/about',
            name: 'about',
            component: About
        },
        {
            path: '/library',
            name: 'library',
            component: Library,
            meta: {
                middleware: [authenticated]
            }
        },
        {
            path: '/library/:product',
            component: Product,
            name: 'product',
            redirect: '/library/:product/contents',
            meta: {
                middleware: [authenticated]
            },
            children: [
                {
                    path: 'contents',
                    name: 'product.contents',
                    component: ProductContents
                },
                {
                    path: 'exercises',
                    name: 'product.exercises',
                    component: ProductExercises
                },
                {
                    path: 'assignments',
                    name: 'product.assignments',
                    component: ProductAssignments
                },
                {
                    path: 'simulator',
                    name: 'product.simulator',
                    component: ProductSimulator
                }
            ]
        },
        {
            path: '/library/:product/chapters/:chapter',
            component: Chapter,
            name: 'chapter',
            meta: {
                middleware: [authenticated]
            }
        },
        {
            path: '/library/:product/assignments/:assignment',
            component: Assignment,
            name: 'assignment',
            meta: {
                middleware: [authenticated]
            }
        }
    ]
});

function nextFactory(context, middleware, index) {
    const subsequentMiddleware = middleware[index];
    // If no subsequent Middleware exists,
    // the default `next()` callback is returned.
    if(!subsequentMiddleware){
        return context.next;
    }

    return (...parameters) => {
        // Run the default Vue Router `next()` callback first.
        context.next(...parameters);
        // Than run the subsequent Middleware with a new
        // `nextMiddleware()` callback.
        const nextMiddleware = nextFactory(context, middleware, index + 1);
        subsequentMiddleware({ ...context, next: nextMiddleware });
    };
}

router.beforeEach((to, from, next) => {
    const middleware = [];

    for(const match of to.matched){
        if(match.meta.middleware){
            middleware.push(...(Array.isArray(match.meta.middleware) ? match.meta.middleware : [match.meta.middleware]));
        }
    }

    if(middleware.length){
        const context = {from, next, router, to};
        const nextMiddleware = nextFactory(context, middleware, 1);
        return middleware[0]({...context, next: nextMiddleware});
    }

    return next();
});

export default router;
